import { useCallback, useEffect, useMemo, useState } from "react";
import { IMenuOption } from "../../../types/global";
import { useTranslation } from "react-i18next";
import { useStateContext } from "../../../contexts/auth-context";

export const useCustomerDepositOption = (status?: string | null) => {
  const { t } = useTranslation();
  const {
    state: { permissions },
  } = useStateContext();

  const defaultOptions: IMenuOption[] = useMemo(
    () => [
      {
        value: "แก้ไข",
        disabled: false,
      },
      {
        value: "คัดลอก",
        disabled: false,
      },
      {
        value: "ยกเลิก",
        disabled: false,
      },
    ],
    []
  );

  const editOption = defaultOptions[0];
  const copyOption = defaultOptions[1];
  const cancelOption = defaultOptions[2];

  const externalRefOption: IMenuOption = useMemo(
    () => ({ value: t("sales.external_reference_no"), disabled: false }),
    [t]
  );

  const canUpdate = useCallback(() => {
    return permissions?.customer_deposit?.update;
  }, [permissions]);

  const canCancel = useCallback(() => {
    return permissions?.customer_deposit?.cancel;
  }, [permissions]);

  const getOptionsForStatus = useCallback(
    (status?: string | null) => {
      const isCreate = permissions?.customer_deposit?.create;

      if (["draft"].includes(status || "")) {
        if (canCancel()) {
          return isCreate ? [copyOption, cancelOption] : [cancelOption];
        }
        return isCreate ? [copyOption] : [];
      }

      if (["cancelled"].includes(status || "")) {
        return isCreate ? [copyOption] : [];
      }

      if (["finished"].includes(status || "")) {
        return isCreate
          ? canUpdate()
            ? [externalRefOption, copyOption]
            : [copyOption]
          : [];
      }

      if (["wait_deduction"].includes(status || "")) {
        if (canUpdate()) {
          return isCreate
            ? [externalRefOption, ...defaultOptions]
            : [externalRefOption, editOption, cancelOption];
        }
        if (canUpdate()) {
          return isCreate
            ? [externalRefOption, copyOption, editOption]
            : [externalRefOption, editOption];
        }
        if (canCancel()) {
          return isCreate ? [copyOption, cancelOption] : [cancelOption];
        }
        return isCreate ? [copyOption] : [];
      }

      if (["partially_deduction"].includes(status || "")) {
        if (canUpdate()) {
          return isCreate
            ? [externalRefOption, copyOption, editOption]
            : [externalRefOption, editOption];
        }
        return isCreate ? [copyOption] : [];
      }

      // Default case when no specific status matches
      return canUpdate() && canCancel()
        ? isCreate
          ? [externalRefOption, ...defaultOptions]
          : [externalRefOption, editOption, cancelOption]
        : canUpdate()
        ? isCreate
          ? [externalRefOption, copyOption, editOption]
          : [externalRefOption, editOption]
        : canCancel()
        ? isCreate
          ? [copyOption, cancelOption]
          : [cancelOption]
        : [];
    },
    [
      permissions?.customer_deposit?.create,
      canUpdate,
      canCancel,
      defaultOptions,
      editOption,
      cancelOption,
      copyOption,
      externalRefOption,
    ]
  );

  const [selectModifyOptions, setSelectedModifyOptions] =
    useState<IMenuOption[]>(defaultOptions);

  useEffect(() => {
    const options = getOptionsForStatus(status);
    setSelectedModifyOptions(options);
  }, [status, getOptionsForStatus]);

  return selectModifyOptions;
};
